// Day-of-week illustration registry. Each day maps to a primary image
// in /public/img/days/. Sunday, Monday, and Saturday also have an
// `-alt` variant — these are RARE-SPAWN rewards. The alt is not
// persistent: each time the caller picks an image (typically once per
// cursor change to that day), a fresh roll decides whether the alt
// shows up. Once the user leaves the day and comes back, the next
// roll is independent — the alt is gone unless luck strikes again.
//
// Source PNGs were converted to WebP (cwebp -q 80, max 1100px) for ~95%
// size reduction over the originals. Originals live in
// public/img/days/_original/ if you ever need to re-encode.

const PRIMARY = [
  "/img/days/sunday.webp",     // 0
  "/img/days/monday.webp",     // 1
  "/img/days/tuesday.webp",    // 2
  "/img/days/wednesday.webp",  // 3
  "/img/days/thursday.webp",   // 4
  "/img/days/friday.webp",     // 5
  "/img/days/saturday.webp",   // 6
];

// Days that have an alt-spawn variant. Indices match getDay().
const ALTS = {
  0: "/img/days/sunday-alt.webp",
  1: "/img/days/monday-alt.webp",
  6: "/img/days/saturday-alt.webp",
};

// 0.5% per roll — 1 in 200. Rare enough to feel like a small reward
// when it appears; common enough that an attentive user will catch
// one every few weeks of normal use. Adjust here if you want it
// rarer (try 0.001 = 1 in 1000) or showier (try 0.02 = 1 in 50).
export const ALT_RATE = 0.005;

// The primary URL for a given date — always the same one.
export function primaryFor(date) {
  if (!(date instanceof Date)) return null;
  return PRIMARY[date.getDay()] || null;
}

// The alt URL for a given date, or null if the day has no alt.
export function altFor(date) {
  if (!(date instanceof Date)) return null;
  return ALTS[date.getDay()] || null;
}

// Fresh random roll. true ~12.5% of calls. Use this directly when you
// want to gate something on the alt's presence; otherwise call
// pickDayImage which does the roll + lookup in one shot.
export function rollAlt() {
  return Math.random() < ALT_RATE;
}

// Return the URL to render for `date`, doing one fresh roll. If the
// day has no alt, this is just the primary. If it has one, the alt
// shows up with probability ALT_RATE — every other call lands on
// the primary.
//
// Caller should memoize the result for the lifetime of a single
// "visit" (mount, day-cursor change) so the image doesn't flicker
// mid-view. See Plan.jsx for the canonical wrapping pattern.
export function pickDayImage(date) {
  const primary = primaryFor(date);
  const alt = altFor(date);
  if (alt && rollAlt()) return alt;
  return primary;
}

// Backwards-compat alias for older callers. Identical to pickDayImage —
// performs a fresh roll on each call, so memoize externally.
export const dayImageFor = pickDayImage;
